import { useState, useEffect, useRef } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { useOnboarding } from "./useOnboarding";
import { ONBOARDING_MESSAGES, type WalletType } from "@/constants/onboarding";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { getReferralCodeWithSource } from "@/hooks/referral/referralCodeSources";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useSnackbar } from "notistack";

export const useCreateAccountStep = () => {
  const { handleWalletConnect } = useOnboarding();
  const { hubUser, authenticated, logout } = useHubUser();
  const { enqueueSnackbar } = useSnackbar();
  const { trackEvent } = useMetricsEvents();

  const [isConnecting, setIsConnecting] = useState(false);
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [hasCodeFromUrl, setHasCodeFromUrl] = useState(false);

  // Ref for auto-scrolling
  const referralInputRef = useRef<HTMLDivElement>(null);

  // Check if user is already connected
  const isWalletConnected = Boolean(authenticated && hubUser);

  // Extract referral code from URL/localStorage on component mount
  useEffect(() => {
    const { code, source } = getReferralCodeWithSource();

    if (code) {
      setReferralCode(code);

      if (source === "url") {
        // Code from URL - hide section, auto-submit will handle it
        setHasCodeFromUrl(true);
      } else {
        // Code from localStorage - show input so user can see/edit
        setShowReferralInput(true);
      }
    }
  }, []);

  const handleWalletConnection = async (walletType: WalletType) => {
    // Track when user clicks wallet connect button during onboarding
    trackEvent(METRICS_EVENTS.ONBOARDING_WALLET_CONNECT_CLICKED, {
      wallet_type: walletType,
    });

    setIsConnecting(true);
    try {
      await handleWalletConnect(walletType);
      // Don't call onContinue - smart progression will handle step advancement
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    // Track when user disconnects wallet during onboarding
    trackEvent(METRICS_EVENTS.ONBOARDING_WALLET_DISCONNECTED, {});

    setIsConnecting(true);
    try {
      logout();
      enqueueSnackbar(ONBOARDING_MESSAGES.WALLET_DISCONNECTED, { variant: "success" });
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (error) {
      enqueueSnackbar(ONBOARDING_MESSAGES.WALLET_DISCONNECT_FAILED, { variant: "error" });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleReferralCodeSave = () => {
    if (!referralCode.trim()) {
      enqueueSnackbar(ONBOARDING_MESSAGES.REFERRAL_CODE_REQUIRED, { variant: "error" });
      return;
    }

    // Track when user saves a referral code during onboarding
    trackEvent(METRICS_EVENTS.ONBOARDING_REFERRAL_CODE_SAVED, {
      referral_code_length: referralCode.trim().length,
    });

    // Save the code to localStorage to be used later when wallet connects
    localStorage.setItem("pendingReferralCode", referralCode.trim());
    setShowReferralInput(false); // Collapse after saving
    enqueueSnackbar(ONBOARDING_MESSAGES.REFERRAL_CODE_SAVED, { variant: "success" });
  };

  const toggleReferralInput = () => {
    const newShowState = !showReferralInput;

    if (newShowState) {
      // Track when user opens the referral code input during onboarding
      trackEvent(METRICS_EVENTS.ONBOARDING_REFERRAL_TOGGLE_OPENED, {});
    }

    setShowReferralInput(newShowState);

    // Auto-scroll to input when expanding
    if (newShowState) {
      setTimeout(() => {
        referralInputRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      }, 100); // Small delay to ensure the input is rendered
    }
  };

  return {
    // State
    isConnecting,
    showReferralInput,
    referralCode,
    setReferralCode,
    isWalletConnected,

    // Ref
    referralInputRef,

    // Computed values
    // Hide referral section if code came from URL (auto-submit handles it).
    // Show section if no URL code so user can enter one manually or see localStorage code.
    hasReferrer: hasCodeFromUrl,
    hubUser,

    // Actions
    handleWalletConnection,
    handleDisconnect,
    handleReferralCodeSave,
    toggleReferralInput,
  };
};
